// import type { MetaData, PartData } from '@enonic/react-components';
import type { IconDefinition } from '@fortawesome/free-brands-svg-icons';
import type { AlertBannerData } from './AlertBannerProps.d';

import React, { useState } from 'react';
import { TooltipProvider } from '@hdir/ui/tooltip';
import { IconWithText } from '/react4xp/shared/IconWithText/IconWithText';
import { ShowMoreButton } from '/react4xp/shared/ShowMoreButton/ShowMoreButton';
import { AlertBannerCard } from '/react4xp/shared/AlertBannerCard/AlertBannerCard';
import { Link } from '@hdir/ui/link';
import { faSignalStream, faBellSlash, faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIconWrapper } from "/react4xp/components/FontAwesomeIconWrapper";


export function AlertBanner(props: {
	data: AlertBannerData;
}): React.JSX.Element {
	const [isOpen, setIsOpen] = useState(false);
	const { data } = props;
	const { alerts, alertCount, alertsListLink } = data;

	// Sorting alerts with type varsling to the top of the list
	const sortedAlerts = [
		...alerts.filter(a => a.alertType === 'varsling'),
		...alerts.filter(a => a.alertType !== 'varsling'),
	];

	const visibleAlerts = isOpen ? sortedAlerts : sortedAlerts.slice(0, 2);

	const handleToggle = (): void => {
		setIsOpen(prevState => !prevState);
	};

	const NotificationCounter = ({ alertCount }: { alertCount: number }): React.JSX.Element => {
		const getTextAndIcon = (): { icon: IconDefinition, text: string } => {
			if (alertCount === 0) {
				return { icon: faBellSlash, text: "Ingen aktive varsler for øyeblikket" };
			} else if (alertCount === 1) {
				return { icon: faSignalStream, text: "1 aktivt varsel" };
			} else {
				return { icon: faSignalStream, text: `${alertCount} aktive varsler` };
			}
		};

		const { icon, text } = getTextAndIcon();

		return (
			<IconWithText
				className='text-xl font-bold'
				icon={icon}
				iconSize='lg'
				text={text}
			/>
		);
	};

	return (
		<TooltipProvider>
			<section className="mt-xl tablet:mt-xxl">
				<div className="w-full flex flex-col gap-md">
					<NotificationCounter alertCount={alertCount} />
					{alertCount > 0 && (
						<>
							<div className="grid grid-cols-1 desktop:grid-cols-2 gap-md tablet:gap-xl">
								{visibleAlerts.map((alert, i) => (
									<AlertBannerCard
										variant={alert.variant}
										alertListLink={alert.alertListLink}
										title={alert.title}
										id={alert.id}
										key={alert.id}
										image={alert.image}
										content={alert.content}
										location={alert.location}
										date={alert.date}
										alertType={alert.alertType}
									/>
								))}
							</div>
							{isOpen && alertsListLink && alertCount > alerts.length && (
								<div className="mt-sm flex justify-end">
									<Link href={alertsListLink} variant="button-primary">
										<span className="inline-flex items-center gap-xs">
											Se alle aktive varsler
											<FontAwesomeIconWrapper icon={faArrowRight} fixedWidth={true}/>
										</span>
									</Link>
								</div>
							)}
							{alerts.length > 2 && (
								<ShowMoreButton onToggle={handleToggle} isOpen={isOpen} ariaId="show-more-alerts" />
							)}
						</>
					)}
				</div>
			</section>
		</TooltipProvider>
	);
}
